import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useRef, useState } from 'react'
import { api } from '../utils/api'
import { Plus, Printer, Pencil, Trash2, FileText, Search, Paperclip, Upload, X, File, ImageIcon, RefreshCw, ExternalLink } from 'lucide-react'

interface Attachment {
  id: string; filename: string; originalName: string
  url: string; size: number | null; mimeType: string | null; createdAt: string
}
interface DeliveryDoc {
  id: string; docNumber: string | null; employeeName: string | null
  employeeId: string | null; department: string | null; location: string | null
  serialNumber: string | null; contractStart: string | null; contractEnd: string | null
  createdAt: string; docStatus: string | null
  asset: { id: string; assetCode: string | null; name: string; type: string } | null
  attachments: Attachment[]
}

const DOC_STATUS: Record<string, { label: string; cls: string }> = {
  PENDING:   { label: 'รอแนบไฟล์',  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  COMPLETED: { label: 'มีใบส่งมอบ', cls: 'bg-green-50 text-green-700 border-green-200' },
  CANCELLED: { label: 'ยกเลิก',     cls: 'bg-red-50 text-red-600 border-red-200' },
}

function StatusSelect({ docId, value, isAdmin }: { docId: string; value: string | null; isAdmin: boolean }) {
  const qc = useQueryClient()
  const updateMut = useMutation({
    mutationFn: (docStatus: string) => api.patch(`/deliveries/${docId}/status`, { docStatus }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  })
  const cur = value || 'PENDING'
  const cfg = DOC_STATUS[cur] || DOC_STATUS.PENDING
  return (
    <select
      value={cur}
      onChange={e => updateMut.mutate(e.target.value)}
      disabled={!isAdmin || updateMut.isPending}
      className={`text-xs font-medium px-2 py-0.5 rounded-full border cursor-pointer disabled:cursor-default ${cfg.cls}`}
    >
      {Object.entries(DOC_STATUS).map(([v, c]) => (
        <option key={v} value={v}>{c.label}</option>
      ))}
    </select>
  )
}

const BASE = (import.meta as any).env?.VITE_API_BASE || ''
const fileUrl = (url: string) => `${BASE}${url}`

function AttachmentModal({ doc, onClose }: { doc: DeliveryDoc; onClose: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const deleteMut = useMutation({
    mutationFn: (attId: string) => api.delete(`/deliveries/${doc.id}/attachments/${attId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  })

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    const fd = new FormData()
    Array.from(files).forEach(f => fd.append('files', f))
    try {
      await api.post(`/deliveries/${doc.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['deliveries'] })
    } catch (e: any) {
      alert(e.response?.data?.message || 'อัปโหลดไม่สำเร็จ')
    } finally { setUploading(false) }
  }

  const isPdf = (mime: string | null) => mime === 'application/pdf'
  const fmtSize = (s: number | null) => !s ? '' : s < 1024 * 1024 ? `${(s / 1024).toFixed(0)} KB` : `${(s / 1024 / 1024).toFixed(1)} MB`

  // Use latest from query cache
  const { data: docs = [] } = useQuery<DeliveryDoc[]>({ queryKey: ['deliveries'], queryFn: () => api.get('/deliveries').then(r => r.data) })
  const latest = docs.find(d => d.id === doc.id) || doc
  const attachments = latest.attachments

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Paperclip size={16}/> ไฟล์แนบ</h2>
            <p className="text-xs text-gray-400 mt-0.5">{doc.docNumber || 'ใบส่งมอบ'} · {doc.employeeName || '—'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files) }}
          onClick={() => fileRef.current?.click()}
          className={`mx-5 mt-4 border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors flex-shrink-0 ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
          }`}>
          {uploading
            ? <div className="flex items-center justify-center gap-2 text-blue-600"><RefreshCw size={16} className="animate-spin"/> กำลังอัปโหลด...</div>
            : <>
                <Upload size={20} className="mx-auto text-gray-400 mb-1"/>
                <p className="text-sm font-medium text-gray-600">คลิกหรือลากไฟล์มาวางที่นี่</p>
                <p className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG · ไม่เกิน 20 MB ต่อไฟล์</p>
              </>}
          <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
            className="hidden" onChange={e => uploadFiles(e.target.files)}/>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {attachments.length === 0 && (
            <div className="text-center text-gray-400 py-6 text-sm">ยังไม่มีไฟล์แนบ</div>
          )}
          {attachments.map(att => (
            <div key={att.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl group">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isPdf(att.mimeType) ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
              }`}>
                {isPdf(att.mimeType) ? <File size={16}/> : <ImageIcon size={16}/>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{att.originalName}</p>
                <p className="text-xs text-gray-400">{fmtSize(att.size)} · {new Date(att.createdAt).toLocaleDateString('th-TH')}</p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <a href={fileUrl(att.url)} target="_blank" rel="noreferrer"
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="เปิดไฟล์">
                  <ExternalLink size={14}/>
                </a>
                <button onClick={() => { if (confirm('ลบไฟล์นี้?')) deleteMut.mutate(att.id) }}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="ลบ">
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">ปิด</button>
        </div>
      </div>
    </div>
  )
}

export default function DeliveryListPage({ isAdmin }: { isAdmin: boolean }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [attachDoc, setAttachDoc] = useState<DeliveryDoc | null>(null)

  const { data: docs = [], isLoading } = useQuery<DeliveryDoc[]>({
    queryKey: ['deliveries'],
    queryFn: () => api.get('/deliveries').then(r => r.data),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/deliveries/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  })

  const filtered = docs.filter(d =>
    !search || [d.docNumber, d.employeeName, d.employeeId, d.department, d.asset?.assetCode, d.asset?.name]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  )

  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('th-TH') : '—'

  return (
    <div className="h-full flex flex-col gap-3 p-4 overflow-hidden">
      <div className="flex items-center justify-between gap-3 flex-wrap flex-shrink-0">
        <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FileText size={18} className="text-blue-600"/> ใบส่งมอบทรัพย์สิน
        </h1>
        {isAdmin && (
          <button onClick={() => navigate('/deliveries/new')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus size={14}/> สร้างใบส่งมอบ
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-3 py-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="ค้นหา เลขที่เอกสาร, ชื่อพนักงาน, รหัส, หน่วยงาน..."
              className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <p className="text-sm text-gray-500 whitespace-nowrap">{filtered.length} รายการ</p>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">กำลังโหลด...</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400 gap-2">
            <FileText size={32} className="text-gray-200"/>
            <p className="text-sm">ยังไม่มีใบส่งมอบ</p>
            {isAdmin && <button onClick={() => navigate('/deliveries/new')} className="text-sm text-blue-600 hover:underline">+ สร้างใบส่งมอบแรก</button>}
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                <tr>
                  {['เลขที่เอกสาร','Asset','รหัสพนักงาน','ชื่อพนักงาน','หน่วยงาน','สถานที่','หมดสัญญา','สถานะ','ไฟล์แนบ','วันที่สร้าง',''].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 font-mono text-blue-600 font-medium whitespace-nowrap">
                      {doc.docNumber || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      {doc.asset ? (
                        <div>
                          <p className="font-medium text-gray-900 text-xs">{doc.asset.name}</p>
                          {doc.asset.assetCode && <p className="text-xs font-mono text-blue-500">{doc.asset.assetCode}</p>}
                        </div>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs whitespace-nowrap">{doc.employeeId || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-900 text-xs whitespace-nowrap">{doc.employeeName || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs whitespace-nowrap">{doc.department || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs whitespace-nowrap">{doc.location || '—'}</td>
                    <td className="px-3 py-1.5 text-gray-600 text-xs whitespace-nowrap">{fmt(doc.contractEnd)}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      <StatusSelect docId={doc.id} value={doc.docStatus} isAdmin={isAdmin}/>
                    </td>
                    <td className="px-3 py-1.5">
                      <button onClick={() => setAttachDoc(doc)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors hover:bg-gray-100"
                        title="จัดการไฟล์แนบ">
                        <Paperclip size={12} className={doc.attachments.length > 0 ? 'text-blue-500' : 'text-gray-400'}/>
                        {doc.attachments.length > 0
                          ? <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{doc.attachments.length}</span>
                          : <span className="text-gray-400">แนบ</span>}
                      </button>
                    </td>
                    <td className="px-3 py-1.5 text-gray-400 text-xs whitespace-nowrap">{fmt(doc.createdAt)}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => window.open(`/deliveries/${doc.id}/print`, '_blank')}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="พิมพ์">
                          <Printer size={14}/>
                        </button>
                        {isAdmin && <>
                          <button onClick={() => navigate(`/deliveries/${doc.id}/edit`)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="แก้ไข">
                            <Pencil size={14}/>
                          </button>
                          <button onClick={() => { if (confirm(`ลบใบส่งมอบของ "${doc.employeeName || doc.docNumber || doc.id}" ใช่หรือไม่?`)) deleteMut.mutate(doc.id) }}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="ลบ">
                            <Trash2 size={14}/>
                          </button>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {attachDoc && <AttachmentModal doc={attachDoc} onClose={() => setAttachDoc(null)}/>}
    </div>
  )
}
